/**
 * InviteGuestModal
 * Modal voor het uitnodigen van gasten voor netwerkclub events
 */

import React, { useState } from 'react';
import { auth, db } from '../../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { networkClubService } from '../../../services/networkClubService';
import { NetworkClubGuest } from '../../../types/networkclub';
import { Icon } from '../../icons';
import { X, UserPlus, Mail, Phone, Building2, FileText, Loader } from '../../icons';

interface InviteGuestModalProps {
  clubId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const InviteGuestModal: React.FC<InviteGuestModalProps> = ({ clubId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.name.trim()) {
      setError('Naam is verplicht');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is verplicht');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Ongeldig email adres');
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError('Je moet ingelogd zijn');
        setLoading(false);
        return;
      }

      const guestData: Omit<NetworkClubGuest, 'id' | 'invitedAt'> = {
        clubId,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        company: formData.company.trim() || undefined,
        invitedBy: user.uid,
        status: 'invited',
        notes: formData.notes.trim() || undefined
      };

      // Maak guest document aan
      const guestRef = await addDoc(collection(db, 'networkClubGuests'), {
        ...guestData,
        invitedAt: serverTimestamp()
      });

      if (guestRef.id) {
        onSuccess();
      } else {
        setError('Kon gast niet uitnodigen');
      }
    } catch (err: any) {
      console.error('Error inviting guest:', err);
      setError(err.message || 'Fout bij uitnodigen van gast');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5 text-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Icon icon={UserPlus} size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-bold">Gast uitnodigen</h3>
                <p className="text-white/80 text-sm">Nodig een gast uit voor een event</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Icon icon={X} size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Naam */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Naam <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon icon={UserPlus} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Volledige naam"
                required
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon icon={Mail} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="email@voorbeeld.nl"
                required
              />
            </div>
          </div>

          {/* Telefoon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefoon
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon icon={Phone} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="+31 6 12345678"
              />
            </div>
          </div>

          {/* Bedrijf */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bedrijf
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon icon={Building2} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Bedrijfsnaam"
              />
            </div>
          </div>

          {/* Notities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notities
            </label>
            <div className="relative">
              <div className="absolute top-2 left-3 pointer-events-none">
                <Icon icon={FileText} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                rows={3}
                placeholder="Optionele notities over de gast..."
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={loading}
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Icon icon={Loader} size={16} strokeWidth={1.5} className="animate-spin" />
                  <span>Verzenden...</span>
                </>
              ) : (
                <>
                  <Icon icon={UserPlus} size={16} strokeWidth={1.5} />
                  <span>Uitnodigen</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteGuestModal;
